import {errorDetail} from './errorDetail';

export const fmtCurrency = (value) => {
  if (value == null || Number.isNaN(value)) return 'N/A';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  } catch {
    return `$${Math.round(value)}`;
  }
};

export const fmtPhone = (value) => {
  if (!value) return 'N/A';
  return String(value).trim();
};

export const normalizeEmails = (emails) => {
  if (!emails || !Array.isArray(emails)) return [];
  return emails
    .map((email) => String(email ?? '').trim())
    .filter(Boolean);
};

/** Display label for a company address object or string. */
export const formatLocationLabel = (location) => {
  if (!location) return '';
  if (typeof location === 'string') return location.trim();
  const fullAddress = String(location.fullAddress || '').trim();
  if (fullAddress) return fullAddress;
  return [location.city, location.region1 || location.state]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(', ');
};

/**
 * Unique location labels for a company (primary first, then otherLocations).
 * Used by the spreadsheet Location column (same +N pattern as emails).
 */
export const normalizeCompanyLocations = (company) => {
  if (!company) return [];

  const labels = [];
  const seen = new Set();
  const push = (value) => {
    const label = formatLocationLabel(value);
    if (!label || label.toLowerCase() === 'n/a') return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    labels.push(label);
  };

  if (Array.isArray(company) && !company.primaryLocation) {
    company.forEach(push);
    return labels;
  }

  push(company.primaryLocation);
  if (Array.isArray(company.otherLocations)) {
    company.otherLocations.forEach(push);
  }
  if (Array.isArray(company.locations)) {
    company.locations.forEach(push);
  }

  return labels;
};

/**
 * Collapse duplicate company records (e.g. same stagedId with different locations)
 * into one company, merging otherLocations + emails. Preserves first-seen order.
 */
export const groupCompaniesForSpreadsheet = (companies) => {
  if (!Array.isArray(companies) || companies.length === 0) return [];

  const order = [];
  const byKey = new Map();

  companies.forEach((company, index) => {
    if (!company) return;
    const key =
      company.stagedId != null
        ? `staged:${company.stagedId}`
        : company.alphasearchId != null
          ? `alpha:${company.alphasearchId}`
          : `anon:${index}`;

    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        ...company,
        otherLocations: Array.isArray(company.otherLocations)
          ? [...company.otherLocations]
          : [],
        emails: Array.isArray(company.emails) ? [...company.emails] : company.emails,
      });
      order.push(key);
      return;
    }

    if (company.primaryLocation) {
      existing.otherLocations.push(company.primaryLocation);
    }
    if (Array.isArray(company.otherLocations) && company.otherLocations.length > 0) {
      existing.otherLocations.push(...company.otherLocations);
    }
    if (Array.isArray(company.emails) && company.emails.length > 0) {
      existing.emails = Array.from(
        new Set([...(existing.emails || []), ...company.emails].map((e) => String(e).trim()).filter(Boolean))
      );
    }
  });

  return order.map((key) => byKey.get(key));
};

const INFRASTRUCTURE_ERROR_PATTERN =
  /No local mock|ECONNREFUSED|Network Error|Failed to fetch|ERR_NETWORK|ENOTFOUND|ERR_CONNECTION/i;

export const isInfrastructureApiErrorMessage = (message) =>
  INFRASTRUCTURE_ERROR_PATTERN.test(String(message || ''));

/**
 * Map verify-email failures to controlled cell copy.
 * Raw infrastructure messages stay in development logs via parseApiError.
 */
export const formatVerifyEmailCellError = (error) => {
  const raw = String(error || '').trim();
  if (!raw) return 'Verification failed';
  if (isInfrastructureApiErrorMessage(raw)) return 'Unable to verify';
  if (raw === 'Missing emails' || raw === 'Row not found') return raw;
  if (/credit/i.test(raw)) return raw;
  if (/^No (successful )?verifications?/i.test(raw)) return 'Verification failed';
  if (raw === 'Unable to verify' || raw === 'Verification failed') return raw;
  if (raw === 'Verify Email failed' || raw === 'Failed') return 'Verification failed';
  return 'Verification failed';
};

export const parseApiError = (error, fallback = 'Request failed') => {
  const status = error?.response?.status;
  const detailMessage = errorDetail(error?.response?.data?.detail, '');
  const raw = detailMessage || error?.response?.data?.message || error?.message || fallback;
  if (isInfrastructureApiErrorMessage(raw)) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(raw, error);
    }
    return { status, msg: fallback, technicalMessage: raw };
  }
  return { status, msg: raw, technicalMessage: null };
};

export const extractDomain = (domain) => {
  if (!domain || domain === 'N/A') return null;
  try {
    let value = domain;
    if (!/^https?:\/\//i.test(value)) value = 'http://' + value;
    const url = new URL(value);
    return url.hostname.replace(/^www\./i, '');
  } catch {
    return String(domain).replace(/^https?:\/\//i, '').split('/')[0].replace(/^www\./i, '');
  }
};

export const createSpreadsheetDraftSessionId = () => (
  `draft_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
);
